// Locale + currency aware monetary formatting (Phase 14.3, STEP 6).
//
// The single source of truth for turning a number into a human, region-correct money string:
//   India   → ₹ 1,25,000.00   (en-IN lakh grouping)
//   USA     → $125,000.00     (en-US)
//   Germany → 125.000,00 €    (de-DE)
//   UAE     → AED 125,000.00  (ar-AE / en-AE)
//
// Grouping/symbol placement come from the locale (Intl), precision from the currency master. Nothing is hardcoded.

export interface MoneyFormat {
  /** ISO 4217 code, e.g. "INR". */
  currency?: string;
  /** BCP-47 locale that drives grouping + symbol placement, e.g. "en-IN". */
  locale?: string;
  /** Fixed fraction digits (from the currency master). When omitted, the locale/currency default is used. */
  precision?: number;
  /** Fallback symbol used only when Intl can't resolve the currency. */
  symbol?: string;
  /** Render the ISO code instead of the symbol (e.g. "USD 1,250.00"). */
  showCode?: boolean;
}

const EM_DASH = '—';

/**
 * Format a value as currency. Resilient: an unknown/invalid ISO code falls back to a manual
 * symbol + grouped number rather than throwing, so novel currencies (crypto, regional) still render.
 */
export function formatMoney(value?: number | null, opts: MoneyFormat = {}): string {
  if (value == null || Number.isNaN(value)) return EM_DASH;
  const { currency = 'USD', locale = 'en-US', precision, symbol, showCode } = opts;
  const code = currency.toUpperCase();

  const fraction = precision != null && precision >= 0 && precision <= 6 ? precision : undefined;

  try {
    const nf = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: showCode ? 'code' : 'symbol',
      ...(fraction != null ? { minimumFractionDigits: fraction, maximumFractionDigits: fraction } : {}),
    });
    return nf.format(value);
  } catch {
    // Unknown ISO code (or unsupported by the runtime) — format the number and prefix the symbol/code.
    const digits = fraction ?? 2;
    const num = safeNumber(value, locale, digits);
    const prefix = symbol || code;
    return `${prefix} ${num}`;
  }
}

function safeNumber(value: number, locale: string, precision?: number): string {
  try {
    return new Intl.NumberFormat(locale, precision != null ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : {}).format(value);
  } catch {
    return value.toLocaleString();
  }
}

/** Format an exchange rate with sensible precision (rates need more digits than money). */
export function formatRate(rate?: number | null, locale = 'en-US'): string {
  if (rate == null || Number.isNaN(rate)) return EM_DASH;
  const digits = rate !== 0 && Math.abs(rate) < 1 ? 6 : 4;
  return safeNumber(rate, locale, digits).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}
